import CloseCircle from 'assets/icons/CloseCircle';
import ConfirmPopup from 'components/ui/ConfirmPopup';
import Drawer from 'components/ui/Drawer';
import { BaseButtonSm } from 'components/ui/BaseButton';
import COLOR from 'constants/color';
import { PackAndFocusEntityType, PackEditorMode, PackEditorTabMode } from 'enums';
import { useCallback, useMemo, useRef, useState } from 'react';
import { t } from 'services/i18n.service';
import EditorBody from './EditorBody';
import EditorFooter from './EditorFooter';
import { PackEditorSaveHandler } from 'interfaces/marketplace.interface';
import { usePackAndFocusEditor } from '../../hooks/usePackAndFocusEditor';

const PackAndFocusEditorDrawer = () => {
  const {
    isPackAndFocusEditorOpen,
    editorType,
    editorMode,
    packEditorTabMode,
    closeEditor,
    isEditorDirty
  } = usePackAndFocusEditor();
  const [isCloseWarningOpen, setIsCloseWarningOpen] = useState(false);
  const saveHandlerRef = useRef<PackEditorSaveHandler | null>(null);

  const registerSaveHandler = useCallback(
    (handler: PackEditorSaveHandler | null) => {
      saveHandlerRef.current = handler;
    },
    []
  );

  const headerTitle = useMemo(() => {
    if (editorType === PackAndFocusEntityType.HABIT_PACK) {
      const packLabel =
        packEditorTabMode === PackEditorTabMode.ROUTINE
          ? t('packs.routine_pack')
          : t('packs.standalone_pack');
      return editorMode === PackEditorMode.CREATE
        ? `${t('market.create')} ${packLabel}`
        : `${t('market.edit')} ${packLabel}`;
    }
    if (editorType === PackAndFocusEntityType.FOCUS_TEMPLATE) {
      return editorMode === PackEditorMode.CREATE
        ? `${t('market.create')} ${t('market.focus_mode_template')}`
        : `${t('market.edit')} ${t('market.focus_mode_template')}`;
    }
    return t('save');
  }, [editorType, editorMode, packEditorTabMode]);

  const handleCloseAttempt = useCallback(() => {
    if (!isEditorDirty()) {
      closeEditor();
      return;
    }
    setIsCloseWarningOpen(true);
  }, [closeEditor, isEditorDirty]);

  const handleDiscardChanges = useCallback(() => {
    setIsCloseWarningOpen(false);
    closeEditor();
  }, [closeEditor]);

  const handleSaveFromCloseWarning = useCallback(() => {
    void saveHandlerRef.current?.();
  }, []);

  const handleSaveSuccess = useCallback(() => {
    setIsCloseWarningOpen(false);
  }, []);

  if (!isPackAndFocusEditorOpen) {
    return null;
  }

  return (
    <Drawer
      isOpen={isPackAndFocusEditorOpen}
      onClose={handleCloseAttempt}
      side='right'
      width='92vw'
      className='flex flex-col max-h-[100vh]'
      header={headerTitle}
      closeOnOverlayClick
      closeButton={
        <ConfirmPopup
          open={isCloseWarningOpen}
          onOpenChange={setIsCloseWarningOpen}
          variant='warning'
          placement='left'
          message={t('project.your_changes_will_be_lost_discard_them')}
          cancelLabel={t('project.discard')}
          confirmLabel={t('project.save')}
          onCancel={handleDiscardChanges}
          onConfirm={handleSaveFromCloseWarning}
          className='!w-fit'>
          <BaseButtonSm
            icon={
              <CloseCircle
                styles='~w-4/5 h-auto'
                fill={COLOR.PRIMARY.LIGHT_900}
              />
            }
            variant='icon'
            onClick={handleCloseAttempt}
            aria-label={t('btn_cancel')}
          />
        </ConfirmPopup>
      }
      footer={
        <EditorFooter
          onSaveSuccess={handleSaveSuccess}
          registerSaveHandler={registerSaveHandler}
        />
      }>
      <EditorBody />
    </Drawer>
  );
};

export default PackAndFocusEditorDrawer;
